import { useState } from 'react';
import type { ItemType, PlaceCandidate, TripItem } from '@shared/types';
import { ITEM_TYPES, TYPES_WITHOUT_IMAGES } from '@shared/types';
import { api, ApiError } from '../lib/api';
import { PlacePicker } from './PlacePicker';

const TYPE_LABELS: Record<ItemType, string> = {
  lodging: 'Stay',
  restaurant: 'Eat',
  activity: 'Do',
  tip: 'Tip',
};

/** Inline editor for one existing item: title, note, type, place, image. */
export function ItemEditor({
  item,
  tripId,
  bias,
  onSaved,
  onCancel,
}: {
  item: TripItem;
  tripId: string;
  bias?: { lat: number; lng: number } | null;
  onSaved: (updated: TripItem) => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<ItemType>(item.type);
  const [title, setTitle] = useState(item.ogTitle ?? '');
  const [note, setNote] = useState(item.note ?? '');
  const [url, setUrl] = useState(item.url ?? '');
  const [place, setPlace] = useState<{ place: PlaceCandidate; photoRef: string | null } | null>(
    null,
  );
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showsImage = !TYPES_WITHOUT_IMAGES.includes(type);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const patch: Parameters<typeof api.updateItem>[2] = {
        type,
        ogTitle: title.trim() || null,
        note: note.trim() || null,
        url: url.trim() || null,
      };

      if (place) {
        const p = place.place;
        if (p.placeId) {
          patch.placeId = p.placeId;
          patch.placeName = p.name;
          patch.placeAddress = p.address;
          patch.mapUrl = p.mapUrl;
          patch.lat = p.lat;
          patch.lng = p.lng;
          if (!title.trim()) patch.ogTitle = p.name;
        }
        // Places photos come back as a photo reference; a manually pasted
        // image comes back as a plain URL. Forward whichever we have — keying
        // the URL branch on "no placeId" would drop the image for a Places
        // result whose photo reference we could not parse.
        if (place.photoRef) patch.placePhotoRef = place.photoRef;
        else if (p.photoUrl) patch.imageUrl = p.photoUrl;
      }

      onSaved(await api.updateItem(tripId, item.id, patch));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <div className="rounded-note bg-raised p-4">
      <div className="flex flex-wrap gap-1.5">
        {ITEM_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`font-util text-meta font-medium rounded-btn px-3 py-1.5 transition-colors ${
              type === t ? 'bg-accent text-surface' : 'bg-surface text-muted hover:text-ink'
            }`}
          >
            {TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <input
        className="field mt-3"
        placeholder={type === 'tip' ? 'The tip, in a few words' : 'Name of the place'}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <textarea
        className="field mt-2 min-h-[5rem] resize-y font-body"
        placeholder={
          type === 'tip'
            ? 'What should they know?'
            : 'What would you tell a friend about this place?'
        }
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <input
        className="field mt-2"
        placeholder="Link (optional)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        inputMode="url"
      />

      {showsImage && (
        <div className="mt-3">
          {place ? (
            <div className="flex items-center gap-2">
              {place.place.photoUrl && (
                <img
                  src={place.place.photoUrl}
                  alt=""
                  className="h-12 w-12 rounded-img object-cover"
                />
              )}
              <span className="min-w-0 flex-1 font-util text-meta text-ink">
                {place.place.name || 'Image selected'}
                {place.place.address && (
                  <span className="block truncate text-muted">{place.place.address}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setPlace(null)}
                className="btn-quiet !py-1 !text-meta"
              >
                Clear
              </button>
            </div>
          ) : picking ? (
            <PlacePicker
              initialQuery={title || item.ogTitle || ''}
              bias={bias}
              onPick={(p, ref) => {
                setPlace({ place: p, photoRef: ref });
                setPicking(false);
              }}
              onCancel={() => setPicking(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="btn-quiet !py-1.5 !text-meta"
            >
              {item.ogImage ? 'Change photo or place' : 'Find a photo and map link'}
            </button>
          )}
        </div>
      )}

      {error && <p className="mt-2 font-util text-meta text-[#B4462F]">{error}</p>}

      <div className="mt-3 flex gap-1">
        <button type="button" onClick={save} disabled={busy} className="btn-primary disabled:opacity-60">
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} className="btn-quiet">
          Cancel
        </button>
      </div>
    </div>
  );
}
